'use client';

import { type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { TypographyCaption } from '@heidi/design-system/typography';

import { getDictationModalQueuePorts } from '../../../../dictation-modal-queue-ports';
import { useDictationLogsSpotlightTracking } from '../../hooks/use-dictation-logs-spotlight-tracking';
import { DICTATION_LOGS_SPOTLIGHT_MODAL_ID } from './constants';

interface DictationLogsSpotlightProps {
  /**
   * The nav item element this spotlight points at. Must render the
   * sidebar's Dictate-history button; the spotlight wraps it so the popover
   * stays pinned through sidebar mode changes (icon-only ↔ label-icons).
   */
  children: ReactNode;
  /**
   * Merge the popover anchor onto `children` instead of wrapping it in a div.
   * Set when the child is a list item (`SidebarMenuItem`) sitting directly in a
   * shared `<ul>` — otherwise the anchor inserts `ul > div > li`, which drops the
   * item from the list for assistive tech. The child must forward a ref
   * (`SidebarMenuItem` does; `NavItem` does not, so leave this off there).
   */
  asChild?: boolean;
  /**
   * Whether this mount may show the popover. Several nav anchors can wrap the
   * same queued spotlight; exactly one should pass `true` at a time.
   */
  enabled?: boolean;
  /**
   * The app's Dictate-history flag getter, called at render. A thunk rather
   * than a boolean so a mid-session flip is read where it was read before.
   */
  isDictationLogsEnabled: () => boolean;
}

/**
 * One-shot Dictate history awareness spotlight. Anchors to whichever
 * nav-item element it wraps in `children`, which lets the popover pin
 * correctly even when the sidebar's render branch swaps as the viewport
 * crosses the icon-only breakpoint. Visibility is owned by the modal queue
 * (`launchModalsAtom` enqueues `DICTATION_LOGS_SPOTLIGHT`).
 */
export function DictationLogsSpotlight({
  children,
  asChild = false,
  enabled = true,
  isDictationLogsEnabled,
}: DictationLogsSpotlightProps) {
  const intl = useIntl();
  const { isOpen, handleClose } = getDictationModalQueuePorts().useQueuedModal(
    DICTATION_LOGS_SPOTLIGHT_MODAL_ID
  );
  // FF is also checked at enqueue time; this is belt-and-braces in case it
  // flips off between enqueue and render.
  const flagEnabled = isDictationLogsEnabled();

  const isVisible = enabled && flagEnabled && isOpen;

  const { onGotIt, onCloseButton, onEscape } = useDictationLogsSpotlightTracking({
    isVisible,
    onClose: handleClose,
  });

  if (!isVisible) return <>{children}</>;

  // `open` is hardcoded true; close is driven by explicit user actions
  // (Got it / X / Escape — each calls handleClose). Don't wire
  // onOpenChange — Radix can fire it with `false` during the unmount cycle
  // when the sidebar swaps between icon-only and label-icons mode, which
  // would falsely mark the modal as seen and permanently dismiss it.
  return (
    <Spotlight open>
      <SpotlightAnchor asChild={asChild}>{children}</SpotlightAnchor>
      <SpotlightContent
        align="start"
        alignOffset={-4}
        side="right"
        sideOffset={8}
        // Re-pin every frame. The default "optimized" strategy measures once
        // and only re-pins on scroll/resize, which leaves the popover
        // floating off-anchor for a frame when the sidebar swaps between
        // icon-only and label-icons mode under a viewport resize.
        updatePositionStrategy="always"
        onEscapeKeyDown={onEscape}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        data-testid="dictation-logs-spotlight"
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader>
          <SpotlightTitle>
            <FormattedMessage
              id="S+dJVsHTNr"
              defaultMessage="Dictate history and settings"
            />
          </SpotlightTitle>
          <SpotlightClose
            onClick={onCloseButton}
            aria-label={intl.formatMessage({
              id: 'rbrahOGMC3',
              defaultMessage: 'Close',
            })}
          />
        </SpotlightHeader>
        <SpotlightDescription>
          <TypographyCaption className="text-text-primary-invert">
            <FormattedMessage
              id="DxB0nS9oj1"
              defaultMessage="Customise your shortcut, preferences, and copy previous dictations whenever you need them"
            />
          </TypographyCaption>
        </SpotlightDescription>
        <SpotlightFooter className="justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={onGotIt}
            className="bg-surface text-text-primary hover:bg-background-secondary"
          >
            <FormattedMessage id="NYTGIbSNz5" defaultMessage="Got it" />
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
}
